/**
 * AriaImpactBlock — #6 Fase 3b-2: the "picture of impact" + invariant warnings
 * shown under an ARIA approval card, BEFORE the human confirms.
 *
 * Consumes the (outer, sanitized) preview fields the backend attaches:
 *   - preview.impact             { amountEUR, balanced, affectedAccounts[], counts }
 *   - preview._invariant_warnings [{ code, severity, message }]  (non-blocking)
 *   - preview._validation_warning "…"  (adversarialReview soft advisory)
 *
 * Compact by default: a one-line "moves X € · N accounts" summary + warning
 * rows; the per-account balance before→after table expands on click. Renders
 * nothing when there is neither impact nor a warning (fully backwards-safe with
 * older backends that don't attach these fields).
 */

import { useState } from 'react';
import { AlertTriangle, TrendingUp, ChevronDown } from 'lucide-react';
import { SEVERITY_BADGE_CLASS, normalizeSeverity } from '@/lib/severityHelpers';

function fmtEur(n) {
  const num = Number(n);
  if (n == null || Number.isNaN(num)) return '—';
  return `${num.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
}

export default function AriaImpactBlock({ preview }) {
  const [open, setOpen] = useState(false);
  if (!preview || typeof preview !== 'object') return null;

  const impact = preview.impact && typeof preview.impact === 'object' ? preview.impact : null;
  const warnings = [
    ...(Array.isArray(preview._invariant_warnings) ? preview._invariant_warnings : []),
    ...(preview._validation_warning ? [{ code: 'REVIEW', severity: 'warning', message: preview._validation_warning }] : []),
  ].filter((w) => w && w.message);

  if (!impact && warnings.length === 0) return null;

  const accounts = Array.isArray(impact?.affectedAccounts) ? impact.affectedAccounts : [];
  const hasDetail = accounts.length > 0;

  return (
    <div className="mt-1 max-w-sm space-y-1.5 rounded-lg border border-border bg-muted px-3 py-2 text-xs">
      {warnings.map((w, i) => {
        const sev = normalizeSeverity(w.severity);
        return (
          <div
            key={`${w.code}-${i}`}
            className={`flex items-start gap-1.5 rounded-md border px-2 py-1 text-[11px] ${SEVERITY_BADGE_CLASS[sev]}`}
          >
            <AlertTriangle className="mt-px h-3.5 w-3.5 shrink-0" />
            <span>{w.message}</span>
          </div>
        );
      })}

      {impact && (
        <div>
          <button
            type="button"
            onClick={() => hasDetail && setOpen((o) => !o)}
            className={`flex w-full items-center gap-1.5 text-left text-muted-foreground ${hasDetail ? 'cursor-pointer' : 'cursor-default'}`}
          >
            <TrendingUp className="h-3.5 w-3.5 shrink-0 text-indigo-500" />
            <span className="font-medium">
              {impact.amountEUR != null ? `Mueve ${fmtEur(impact.amountEUR)}` : 'Impacto estimado'}
              {accounts.length > 0 && ` · ${accounts.length} ${accounts.length === 1 ? 'cuenta' : 'cuentas'}`}
            </span>
            {hasDetail && (
              <ChevronDown className={`ml-auto h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            )}
          </button>

          {open && hasDetail && (
            <div className="mt-1.5 overflow-x-auto">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-muted-foreground">
                    <th className="text-left font-normal">Cuenta</th>
                    <th className="text-right font-normal">Antes</th>
                    <th className="text-right font-normal">Después</th>
                    <th className="text-right font-normal">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((a) => (
                    <tr key={a.code} className="border-t border-border">
                      <td className="py-0.5 pr-2 font-mono">{a.code}</td>
                      <td className="text-right tabular-nums">{fmtEur(a.before)}</td>
                      <td className="text-right tabular-nums">{fmtEur(a.after)}</td>
                      <td
                        className={`text-right tabular-nums ${
                          Number(a.delta) >= 0
                            ? 'text-emerald-600 dark:text-emerald-400'
                            : 'text-rose-600 dark:text-rose-400'
                        }`}
                      >
                        {Number(a.delta) >= 0 ? '+' : ''}
                        {fmtEur(a.delta)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
